import axios from './axiosConfig'

export interface DeliveryAssignData {
 driver_id: string
}

export interface DeliveryStatusData {
 status: string
 notes?: string
 proof_photo?: string
 collected_amount?: number
 collection_note?: string
 partial_collection_waiver?: boolean
}

export interface DeliveryTrackingSaleItem {
 id: number
 quantity?: number | string
 name?: string | null
 product?: { id?: number; name?: string | null } | null
}

export interface DeliveryTrackingSaleUser {
 id: number
 name?: string | null
}

export interface DeliveryTrackingSale {
 id: number
 invoice_number?: string | null
 total?: number | string
 customer?: { id?: number; name?: string } | null
 user?: DeliveryTrackingSaleUser | null
 items?: DeliveryTrackingSaleItem[] | null
}

export interface DeliveryTrackingDriver {
 id: string
 name: string
 phone?: string
 status?: string
}

export interface DeliveryTrackingBranch {
 id: string
 name: string
 code?: string | null
}

export interface DeliveryTrackingZone {
 id: string
 name: string
 delivery_fee?: number | string
}

export interface DeliveryTrackingItem {
 id: string
 sale_id: number
 driver_id?: string | null
 branch_id?: string | null
 delivery_address: string
 customer_phone?: string | null
 delivery_fee: number | string
 status: string
 assigned_at?: string | null
 picked_up_at?: string | null
 delivered_at?: string | null
 notes?: string | null
 proof_photo?: string | null
 collected_amount?: number | string
 amount_to_collect?: number | string
 driver_earning?: number | string
 collection_method?: string
 collection_status?: string
 amount_due_from_driver?: number | string
 wait_minutes?: number
 created_at?: string
 updated_at?: string
 sale?: DeliveryTrackingSale | null
 driver?: DeliveryTrackingDriver | null
 branch?: DeliveryTrackingBranch | null
 delivery_zone?: DeliveryTrackingZone | null
}

export interface DeliveryTrackingSummary {
 pending: number
 assigned: number
 picked_up: number
 in_transit: number
 with_driver: number
 active_total: number
 delivered_today: number
 failed_today: number
 returned_today: number
 total_fees_today: number
 total_collected_today: number
 avg_wait_minutes_pending: number
 overdue_pending: number
}

export interface DeliveryTrackingLanes {
 pending: DeliveryTrackingItem[]
 assigned: DeliveryTrackingItem[]
 picked_up: DeliveryTrackingItem[]
 in_transit: DeliveryTrackingItem[]
}

export interface DeliveryTrackingParams {
 branch_id?: string
 driver_id?: string
 from_date?: string
 to_date?: string
 /** Rolling window in hours; when set, backend prefers it over from_date/to_date. */
 since_hours?: number
 search?: string
 include_completed?: number
 delivery_zone_id?: string
 collection_method?: 'cashier' | 'driver' | string
 collection_status?: string
 unassigned?: boolean | 1 | 0
 stage?: 'pending' | 'with_driver' | 'delivered' | 'exception'
}

export interface DeliveryTrackingResponse {
 summary: DeliveryTrackingSummary
 lanes: DeliveryTrackingLanes
 completed_recent: DeliveryTrackingItem[]
 filters?: { from_date?: string; to_date?: string }
}

export const deliveriesAPI = {
 getAll: async (params?: Record<string, unknown>) => {
 const response = await axios.get('/deliveries', { params })
 return response.data
 },

 tracking: async (params?: DeliveryTrackingParams) => {
 const response = await axios.get<{ status: string; data: DeliveryTrackingResponse }>(
 '/deliveries/tracking',
 { params },
 )
 return response.data.data
 },

 getToday: async (params?: Record<string, unknown>) => {
 const response = await axios.get('/deliveries/today', { params })
 return response.data
 },

 getById: async (id: string) => {
 const response = await axios.get(`/deliveries/${id}`)
 return response.data
 },

 assign: async (id: string, data: DeliveryAssignData) => {
 const response = await axios.post(`/deliveries/${id}/assign`, data)
 return response.data
 },

 updateStatus: async (id: string, data: DeliveryStatusData) => {
 const response = await axios.patch(`/deliveries/${id}/status`, data)
 return response.data
 },
}
